/**
 * A escolha de Empresa com "Criar nova empresa" (13.15, chamado de 26/10/2026, itens B e L) — o
 * molde da troca de Empresa do Negócio (`TrocaDaEmpresaDoNegocio.tsx`), para o "+ Pessoa" da tela
 * Pessoas e o "Incluir filial" da ficha do Grupo.
 *
 * - **Escolhida da lista, ou criada no modal** (`ModalDaNovaEmpresa`): chega com o `id` e o nome
 *   exibido, e fica escolhida; o campo é remontado (`chaveDoCampo`) para largar o texto digitado.
 * - **Achada pelo ERP** (`aoEncontrar`, o CNPJ que o Carga Máquina já conhece): chega só com o
 *   `id` (`achada`), e o nome vem da leitura da ficha (`useEmpresa`). **Enquanto lê, `lendo`**:
 *   quem monta trava o envio ("Salvar", "Incluir"). **Se a leitura falha**, `erroDaLeitura` traz
 *   a mensagem e nada fica escolhido; voltar a digitar no campo tira a mensagem.
 * - Fechar o modal sem salvar não cria nada: o modal só devolve a Empresa que o serviço gravou.
 * - Fechado o modal, o foco volta ao campo (pelo `id` dele, depois da remontagem). `limpar` também
 *   o devolve, quando ele estava no campo (o "Incluir" pelo Enter).
 */

import { useEffect, useState } from 'react'

import { ErroDaApi, MENSAGEM_DE_ERRO_DESCONHECIDO } from '@/api/erros'
import { useEmpresa } from './api'
import type { EmpresaEscolhida } from './CampoDeEmpresa'
import { nomeExibidoDaEmpresa } from './nome-da-empresa'

/** O texto do campo enquanto a ficha achada pelo ERP ainda está sendo lida (o do Novo negócio). */
export const LENDO_A_EMPRESA = 'Carregando a Empresa…'

/** O que o campo tem: nada, a escolhida (da lista ou do modal), ou a achada pelo ERP (o `id`). */
type Bruta = null | { achada: false; empresa: EmpresaEscolhida } | { achada: true; id: string }

export interface EscolhaDeEmpresa {
  /** A Empresa pronta para o envio: `null` sem escolha, enquanto lê e se a leitura falhou. */
  escolhida: EmpresaEscolhida | null
  /**
   * O que o campo mostra. A achada pelo ERP ocupa o campo (vazio, com `LENDO_A_EMPRESA`) enquanto
   * lê e também depois de a leitura falhar: é ocupado que o campo avisa a primeira tecla
   * (`aoEscolher(null)`), e é ela que tira a mensagem de erro.
   */
  noCampo: EmpresaEscolhida | null
  /** A ficha achada pelo ERP está sendo lida: o envio de quem monta espera. */
  lendo: boolean
  /** A leitura da ficha achada falhou: a mensagem (verbatim do serviço), e nada escolhido. */
  erroDaLeitura: string | null
  /** A `key` do campo: muda a cada escolha de fora (modal), para ele largar o texto digitado. */
  chaveDoCampo: number
  /** O texto com que o modal da Empresa nova abre, ou `null` com ele fechado. */
  fichaAberta: string | null
  escolher: (empresa: EmpresaEscolhida | null) => void
  abrirAFicha: (texto: string) => void
  fecharAFicha: () => void
  /** A Empresa que o modal gravou: fica escolhida, e o modal fecha. */
  aoCriar: (empresa: EmpresaEscolhida) => void
  /** O `id` da ficha que o ERP achou: fica escolhida, e o nome vem da leitura dela. */
  aoEncontrar: (empresaId: string) => void
  /** Volta ao começo (o formulário fechou, ou o envio terminou). */
  limpar: () => void
}

function mensagemDe(falha: unknown): string {
  return falha instanceof ErroDaApi ? falha.message : MENSAGEM_DE_ERRO_DESCONHECIDO
}

/** `idDoCampo`: o `id` do `<input>` do `CampoDeEmpresa`, que recebe o foco quando o modal fecha. */
export function useEscolhaDeEmpresa(idDoCampo: string): EscolhaDeEmpresa {
  const [bruta, setBruta] = useState<Bruta>(null)
  const [chaveDoCampo, setChaveDoCampo] = useState(0)
  const [fichaAberta, setFichaAberta] = useState<string | null>(null)
  const [pedidoDeFoco, setPedidoDeFoco] = useState(0)

  const lida = useEmpresa(bruta?.achada === true ? bruta.id : undefined)
  const lendo = bruta?.achada === true && lida.data === undefined && !lida.isError
  const falhou = bruta?.achada === true && lida.data === undefined && lida.isError
  const escolhida =
    bruta === null
      ? null
      : !bruta.achada
        ? bruta.empresa
        : lida.data === undefined
          ? null
          : { id: bruta.id, nome: nomeExibidoDaEmpresa(lida.data) }
  const noCampo =
    bruta?.achada === true && escolhida === null ? { id: bruta.id, nome: '' } : escolhida

  useEffect(() => {
    if (pedidoDeFoco > 0) {
      document.getElementById(idDoCampo)?.focus()
    }
  }, [pedidoDeFoco, idDoCampo])

  const deFora = (nova: Bruta) => {
    setBruta(nova)
    setChaveDoCampo((chave) => chave + 1)
    setFichaAberta(null)
    setPedidoDeFoco((pedido) => pedido + 1)
  }

  return {
    escolhida,
    noCampo,
    lendo,
    erroDaLeitura: falhou ? mensagemDe(lida.error) : null,
    chaveDoCampo,
    fichaAberta,
    escolher: (empresa) => {
      setBruta(empresa === null ? null : { achada: false, empresa })
    },
    abrirAFicha: setFichaAberta,
    fecharAFicha: () => {
      setFichaAberta(null)
      setPedidoDeFoco((pedido) => pedido + 1)
    },
    aoCriar: (empresa) => {
      deFora({ achada: false, empresa })
    },
    aoEncontrar: (empresaId) => {
      deFora({ achada: true, id: empresaId })
    },
    limpar: () => {
      const estavaNoCampo = document.activeElement?.id === idDoCampo
      setBruta(null)
      setFichaAberta(null)
      setChaveDoCampo((chave) => chave + 1)
      if (estavaNoCampo) {
        setPedidoDeFoco((pedido) => pedido + 1)
      }
    },
  }
}
